import { useCallback, useMemo } from 'react';

import { useLocalStorageState } from '@/utils/use-local-storage-state';

import { OrgConfigs, useOrgConfig } from './useOrgConfig';

const IFRAME_TRACE_RENDERING_LS_KEY = 'ls:ui:iframe-trace-rendering';

export interface IFrameTraceRenderingConfig {
  enabled: boolean;
  datasetId?: string;
  sessionId?: string;
  url: string;
}

export const useIFrameTraceRendering = (
  datasetId?: string,
  sessionId?: string
) => {
  const [iframeConfigs, setIFrameConfigs] = useLocalStorageState<
    IFrameTraceRenderingConfig[]
  >(IFRAME_TRACE_RENDERING_LS_KEY, []);
  const { value: allowCustomIFrames } = useOrgConfig(
    OrgConfigs.allow_custom_iframes
  );
  const upsertIFrameConfig = useCallback(
    (config: IFrameTraceRenderingConfig) => {
      let added = false;
      const newConfigs = iframeConfigs.reduce((acc, c) => {
        if (
          ((c.datasetId === config.datasetId && c.datasetId) ||
            (c.sessionId === config.sessionId && c.sessionId)) &&
          !added
        ) {
          added = true;
          return [...acc, config];
        }
        return [...acc, c];
      }, [] as IFrameTraceRenderingConfig[]);
      if (!added) {
        newConfigs.push(config);
      }
      setIFrameConfigs(newConfigs);
    },
    [iframeConfigs, setIFrameConfigs]
  );
  const removeIFrameConfig = useCallback(() => {
    const newConfigs = iframeConfigs.filter(
      (c) =>
        (c.datasetId !== datasetId && datasetId) ||
        (c.sessionId !== sessionId && sessionId)
    );
    setIFrameConfigs(newConfigs);
  }, [iframeConfigs, datasetId, sessionId, setIFrameConfigs]);

  const currIFrameConfig = useMemo(
    () =>
      iframeConfigs.find(
        (c) =>
          (c.datasetId === datasetId && datasetId) ||
          (c.sessionId === sessionId && sessionId)
      ),
    [iframeConfigs, datasetId, sessionId]
  );

  return allowCustomIFrames
    ? {
        upsertIFrameConfig,
        removeIFrameConfig,
        currIFrameConfig,
      }
    : {
        upsertIFrameConfig: () => {},
        removeIFrameConfig: () => {},
        currIFrameConfig: undefined,
      };
};
